import { StatusCode } from "api/protocols";
import {
  GetReservationLocation,
  GetReservationModuleAll,
  PutReservationLocation,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import axios, { CancelToken } from "axios";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Page from "components/page/Page";
import SelectNew from "components/select/SelectNew";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { showError, showSuccess } from "redux/actions/ui";
import { ILocation, IModule } from "redux/reducers/models/reservations";
import { useTranslation } from "react-i18next";

interface IState {
  title: string;
  moduleId: number;
  modules: Array<IModule>;
  location: ILocation;
}

export default function LocationEdit() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { authStore } = useAppSelector(store => store);
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<IState>({
    title: "",
    moduleId: null,
    modules: undefined,
    location: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void getLocation(source.token);
    void getModules(source.token);

    return () => source.cancel();
  }, []);

  async function getLocation(cancelToken: CancelToken) {
    const res = await GetReservationLocation({ id: Number(id) }, true, cancelToken);
    if (res?.status !== StatusCode.OK || res?.data?.length < 1) {
      dispatch(showError(t("Error getting location")));
      history.push("/admin/settings/reservations/locations");
      return;
    }
    const location = res?.data[0];
    setState(prevState => ({
      ...prevState,
      location: location,
      title: location?.title,
      moduleId: location?.reservation_module_id,
    }));
  }

  async function getModules(cancelToken: CancelToken) {
    const res = await GetReservationModuleAll(null, false);
    if (res.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      dispatch(showError(t("Error getting modules")));
    }

    setState(prevState => ({
      ...prevState,
      modules: res?.status !== StatusCode.OK ? [] : res?.data,
    }));
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function unsavedChangesExist() {
    if (!state?.location) {
      return false;
    }
    return state?.title !== state?.location?.title || state?.moduleId !== state?.location?.reservation_module_id;
  }

  async function saveLocation() {
    if (!state?.title || !state?.moduleId) {
      dispatch(showError(t("Please fill all fields before saving")));
      return;
    }
    const res = await PutReservationLocation(
      { id: Number(id), title: state.title, reservation_module_id: state.moduleId },
      true,
    );
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating location")));
      return;
    }

    dispatch(showSuccess(t("Location updated successfully")));
    history.push("/admin/settings/reservations/modules");
  }

  function cancelUnsavedChanges() {
    setState(prevState => ({
      ...prevState,
      title: prevState?.location?.title,
      moduleId: prevState?.location?.reservation_module_id,
    }));
  }

  return (
    <Page
      title={`${t("Edit Location:")} ${
        state?.location?.title ?? ""
      }`}
      narrow
      primaryAction={{
        content: t("Save"),
        action: () => saveLocation(),
        disabled: !state.title || !state.moduleId || !authStore?.user?.permissions?.reservations_location_edit,
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Modules"),
          url: "/admin/settings/reservations/modules",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveLocation,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Title")}
                placeholder={t("Title")}
                value={state.title}
                onChange={handleInputChange}
                id="title"
                disabled={!authStore?.user?.permissions?.reservations_location_edit}
              />
              <SelectNew
                id={"id"}
                value={state.moduleId}
                onChange={value => setState(prevState => ({ ...prevState, moduleId: value }))}
                label={t("Module")}
                isLoading={state.modules === undefined}
                options={state.modules?.map(module => ({
                  id: module.id,
                  label: module.title,
                }))}
                searchable
                placeholder={t("Search modules...")}
                disabled={!authStore?.user?.permissions?.reservations_location_edit}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
